import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import SmallTabs from '@node-space/storybook-components/dist/SmallTabs'
import { PathNames } from 'constants/General'
import { useCurrencyOptions } from 'hooks/useCurrencyOptions'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ApprovalStatuses, PendingTransactionsResponse } from 'types/approvals'
import { SelectOption } from 'types/types'
import { ApprovedTransactions } from './Tables/ApprovedTransactions'
import { ExpiredTransactions } from './Tables/ExpiredTransactions'
import { PendingTransactions } from './Tables/PendingTransactions'
import { RejectedTransactions } from './Tables/RejectedTransactions'

interface ApprovalsConfig {
  isFetchingPendingApprovals: boolean
  accountApprovalsPreference: boolean
  pendingApprovalTransactions: PendingTransactionsResponse[]
  isPendingApprovalsError: boolean
  isPendingApprovalSuccess: boolean
  setCheckedRowData: Dispatch<SetStateAction<unknown[]>>
}

interface ApprovalTabsProps {
  approvalsConfig: ApprovalsConfig
  canManageApproval: boolean
  accountReference: string
}

const ApprovalTabs = ({
  approvalsConfig,
  canManageApproval,
  accountReference,
}: ApprovalTabsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currencyOptions: SelectOption[] = useCurrencyOptions()

  const { tab } = queryString.parse(window.location.search) as { tab?: 'APPROVED' | 'REJECTED' }
  const [activeTab, setActiveTab] = useState(ApprovalStatuses.PENDING)

  const pendingApprovalsQueryResults = {
    pendingApprovalTransactions: approvalsConfig?.pendingApprovalTransactions,
    isPendingApprovalsError: approvalsConfig?.isPendingApprovalsError,
    isPendingApprovalSuccess: approvalsConfig?.isPendingApprovalSuccess,
    isFetchingPendingApprovals: approvalsConfig?.isFetchingPendingApprovals,
  }

  const isApprovalPreferenceDisabled = !approvalsConfig?.accountApprovalsPreference

  const tabs = [
    {
      id: ApprovalStatuses.PENDING,
      tabHeader: t('approvals.pending'),
      tabContent: (
        <PendingTransactions
          pendingApprovalsQueryInfo={pendingApprovalsQueryResults}
          canManageApproval={canManageApproval}
          setCheckedRowData={approvalsConfig?.setCheckedRowData}
          filterCurrencyOptions={currencyOptions}
          isApprovalPreferenceDisabled={isApprovalPreferenceDisabled}
        />
      ),
      onClick: () => {
        queryClient.invalidateQueries(
          reactQueryKeys.approvalsTransactions(accountReference, ApprovalStatuses.PENDING)
        )
        navigate(`${PathNames.APPROVALS}`, { replace: true })
        setActiveTab(ApprovalStatuses.PENDING)
      },
    },
    {
      id: ApprovalStatuses.APPROVED,
      tabHeader: t('approvals.approved'),
      tabContent: (
        <ApprovedTransactions
          filterCurrencyOptions={currencyOptions}
          accountReference={accountReference}
        />
      ),
      onClick: () => {
        navigate(`${PathNames.APPROVALS}`, { replace: true })
        setActiveTab(ApprovalStatuses.APPROVED)
      },
    },
    {
      id: ApprovalStatuses.REJECTED,
      tabHeader: t('approvals.rejected'),
      tabContent: (
        <RejectedTransactions
          filterCurrencyOptions={currencyOptions}
          accountReference={accountReference}
        />
      ),
      onClick: () => {
        navigate(`${PathNames.APPROVALS}`, { replace: true })
        setActiveTab(ApprovalStatuses.REJECTED)
      },
    },
    {
      id: ApprovalStatuses.EXPIRED,
      tabHeader: t('approvals.expired'),
      tabContent: (
        <ExpiredTransactions
          filterCurrencyOptions={currencyOptions}
          accountReference={accountReference}
        />
      ),
      onClick: () => {
        navigate(`${PathNames.APPROVALS}`, { replace: true })
        setActiveTab(ApprovalStatuses.EXPIRED)
      },
    },
  ]

  useEffect(() => {
    if (tab) {
      setActiveTab(tab)
    }
  }, [tab])

  return <SmallTabs tabs={tabs} currentValueId={activeTab} />
}

export default ApprovalTabs
